import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const SubscribeSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      navigate('/subscribe');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attemptNum = 0) => {
    if (attemptNum >= 10) {  // Increased from 5 to 10 attempts
      setStatus('timeout');
      return;
    }

    try {
      console.log(`Polling payment status (attempt ${attemptNum + 1}/10)...`);
      const response = await axios.get(`${API}/subscription/status/${sessionId}`);
      
      console.log('Status response:', response.data);
      
      if (response.data.payment_status === 'paid') {
        console.log('✅ Payment confirmed!');
        console.log('Stripe Customer ID:', response.data.stripe_customer_id);
        console.log('Stripe Subscription ID:', response.data.stripe_subscription_id);
        
        setStatus('success');
        await refreshUser();
        toast.success('Subscription activated! You can now manage your subscription in Settings.');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        setStatus('pending');
        setTimeout(() => pollPaymentStatus(attemptNum + 1), 3000);  // Increased from 2s to 3s
        setAttempts(attemptNum + 1);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attemptNum < 9) {
        // Retry on error
        setTimeout(() => pollPaymentStatus(attemptNum + 1), 3000);
        setAttempts(attemptNum + 1);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 50%, #80deea 100%)' }}>
      <Card className="w-full max-w-md bg-white shadow-2xl border-0" data-testid="success-card">
        <div className="p-8 text-center">
          {status === 'checking' || status === 'pending' ? (
            <>
              <Loader2 className="w-16 h-16 mx-auto mb-4 text-cyan-500 animate-spin" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                Checking Payment Status...
              </h2>
              <p className="text-gray-600">Please wait while we confirm your payment</p>
              <p className="text-sm text-gray-500 mt-2">Attempt {attempts + 1} of 10</p>
            </>
          ) : status === 'success' ? (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Space Grotesk' }} data-testid="success-title">
                Payment Successful!
              </h2>
              <p className="text-gray-600 mb-6">Your subscription is now active. Thank you for subscribing to Logly!</p>
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600"
                data-testid="go-home-button"
              >
                Go to Home
              </Button>
            </>
          ) : (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <span className="text-3xl">❌</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                {status === 'timeout' ? 'Payment Check Timed Out' : 'Payment Error'}
              </h2>
              <p className="text-gray-600 mb-6">
                {status === 'timeout'
                  ? 'We could not verify your payment. Please check your email for confirmation.'
                  : 'There was an issue processing your payment. Please try again.'}
              </p>
              <Button
                onClick={() => navigate('/subscribe')}
                className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600"
              >
                Back to Subscribe
              </Button>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default SubscribeSuccess;
